// src/components/AccordionSection.jsx

import React, { useId, useState } from "react";
import { FaChevronDown } from "react-icons/fa";
import "../App.css";

/**
 * Two fixes over the previous version:
 *
 * 1. NO HEIGHT CEILING. The old version capped content at 2000px with
 *    overflow:hidden, so anything taller was silently cut off — which is why
 *    the Jewels grid stopped part-way through the artworks once the inner
 *    scroll wrapper was removed. The grid-template-rows 0fr→1fr technique
 *    animates to the content's true height, whatever that is.
 *
 * 2. CORRECT ARIA STATE. The old version called setOpen(isOpen) in an effect;
 *    for uncontrolled accordions isOpen is undefined, so `open` became
 *    undefined and React omitted aria-expanded entirely on first render.
 *    Deriving `open` instead of syncing it means it is always a real boolean.
 *
 * Also swaps <header role="button" tabIndex={0}> for a real <button> inside
 * the heading — Enter/Space, focusability and semantics come for free, and the
 * h2 stays in the document outline for screen readers.
 */
function AccordionSection({ title, children, id, isOpen, onToggle }) {
  const reactId = useId();
  const contentId = `accordion-content-${id || reactId}`;

  const isControlled = isOpen !== undefined && onToggle !== undefined;
  const [selfOpen, setSelfOpen] = useState(false);
  const open = isControlled ? Boolean(isOpen) : selfOpen;

  const handleToggle = () =>
    isControlled ? onToggle() : setSelfOpen((prev) => !prev);

  return (
    <section className="accordion-section" id={id}>
      <h2 className="accordion-heading">
        <button
          type="button"
          className="accordion-trigger"
          aria-expanded={open}
          aria-controls={contentId}
          onClick={handleToggle}
        >
          <span className="accordion-title-text">{title}</span>
          <FaChevronDown className="chevron" aria-hidden="true" />
        </button>
      </h2>

      {/* Content stays mounted so the transition can actually run. The old
          `{open && children}` unmounted it, so it popped in rather than
          animating. */}
      <div
        id={contentId}
        className="accordion-panel"
        data-open={open}
        role="region"
        aria-labelledby={`${contentId}-label`}
        hidden={!open ? undefined : undefined}
      >
        <div className="accordion-panel-inner">{children}</div>
      </div>
    </section>
  );
}

export default AccordionSection;
